import React, { useMemo, useState } from "react";
import { useGlobalData } from "../hooks/useGlobalHooks";
import { OptimizedNumberCell, OptimizedColorCell } from "./OptimizedCell";
import CancelConfirmationModal from "./CancelConfirmationModal";
import AlpacaExitSuccessModal from "./AlpacaExitSuccessModal";
import { AlpacaBulkModifyModal } from "./AlpacaBulkModifyModal";
import apiClient from "../utils/apiClient";
import server from "../utils/serverConfig";
import { X, Pencil } from "lucide-react";
import { toast } from "react-hot-toast";

export function AlpacaOrderComparisonModal({
    isOpen,
    onClose,
    clientData,
    orders = [],
    symbolStrategyBaseQty = {},
    positions = []
}) {
    const { users } = useGlobalData();

    // Selection & Modify State
    const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
    const [modifyModalOpen, setModifyModalOpen] = useState(false);
    const [ordersToModify, setOrdersToModify] = useState([]);

    // Cancel State
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [successModalActionType, setSuccessModalActionType] = useState("Cancel");
    const [ordersToCancel, setOrdersToCancel] = useState([]);
    const [submittedOrders, setSubmittedOrders] = useState([]);
    const [isCancelling, setIsCancelling] = useState(false);

    // Extract underlying prefix from symbol (e.g., "SPY260804C00760000" -> "SPY")
    function normalizeStrategy(strat) {
    if (!strat) return "NO_STRATEGY";
    const s = String(strat).trim().toUpperCase();
    if (s === "NO_STRATEGY" || s === "N/A" || s === "NONE" || s === "" || s === "SIMPLE" || s === "N/A STRATEGY") {
        return "NO_STRATEGY";
    }
    return s;
}

function getAlpacaUnderlying(symbol) {
        if (!symbol) return "";
        const s = symbol.toUpperCase().trim();
        const match = s.match(/^([A-Z]+)\d/);
        return match ? match[1] : s;
    }

    // Extract option strike
    function extractStrike(symbol) {
        if (!symbol) return "N/A";
        const match = symbol.match(/(\d{3,6})(CE|PE|C|P)$/);
        return match ? match[1] : "N/A";
    }

    // Group orders for comparison
    const ordersBySymbolStrategy = useMemo(() => {
        if (!orders || !clientData?.clientId || !symbolStrategyBaseQty) return [];

        const alpacaUsers = (users || []).filter(u => u && String(u.broker).toLowerCase() === "alpaca");
        const alpacaUserIds = new Set(alpacaUsers.map(u => String(u.client_id).toLowerCase()));

        const rows = [];
        const processedKeys = new Set();

        // Get all valid active pending entry orders for this client
        const clientOrders = orders.filter(o => {
            if (!o) return false;
            const cId = o.clientId || o.client_id;
            if (String(cId).toLowerCase() !== String(clientData.clientId).toLowerCase()) return false;

            const isAlpacaBroker = String(o.broker || "").toLowerCase() === "alpaca";
            const isAlpacaUser = alpacaUserIds.has(String(cId).toLowerCase());
            if (!isAlpacaBroker && !isAlpacaUser) return false;

            const sideStr = String(o.side || "").toLowerCase();
            const isBuy = sideStr === "buy" || sideStr === "1";
            const isSell = sideStr === "sell" || sideStr === "2" || sideStr === "-1";
            if (!isBuy && !isSell) return false;

            // Exclude Stop-Loss / Position Exit orders (orders opposite to existing position)
            const strategy = normalizeStrategy(o.strategy);
            const cleanSym = (o.symbol || "").toString().trim().toUpperCase();
            const pos = positions?.find(p => {
                const pId = p.clientId || p.client_id;
                const pSym = (p.symbol || "").toString().trim().toUpperCase();
                const pStrat = normalizeStrategy(p.strategy);
                return String(pId).toLowerCase() === String(clientData.clientId).toLowerCase() &&
                       pSym === cleanSym && pStrat === strategy;
            });

            if (pos) {
                const posNetQty = parseFloat(pos.netQty ?? pos.net_qty ?? pos.qty ?? 0);
                if (posNetQty > 0 && isSell) return false; // SL order for Long
                if (posNetQty < 0 && isBuy) return false;  // SL order for Short
            }

            const statusStr = String(o.status || "").toLowerCase();
            const statusNo = o.status_no !== undefined ? parseInt(o.status_no, 10) : undefined;
            return ["new", "partially_filled", "accepted", "pending_new", "held", "pending_replace", "pending", "open", "not sent"].includes(statusStr) || statusNo === 6;
        });

        clientOrders.forEach(order => {
            const rowUniqueKey = `${order.symbol}_${normalizeStrategy(order.strategy)}_${order.order_id || order.orderId}`;
            const aggregationKey = `${order.symbol}_${normalizeStrategy(order.strategy)}`;

            if (processedKeys.has(rowUniqueKey)) return;
            processedKeys.add(rowUniqueKey);

            // Get base qty info
            const baseQtyInfo = symbolStrategyBaseQty[aggregationKey];
            const baseQty = baseQtyInfo ? baseQtyInfo.baseQty : 0;

            const user = users?.find(u => String(u.client_id).toLowerCase() === String(clientData.clientId).toLowerCase());
            const multiplier = user ? parseFloat(user.multiplier || 1) : 1;
            const expectedQty = multiplier * baseQty;

            // Sum actual qty for this aggregation key
            let actualQty = 0;
            clientOrders.forEach(o => {
                const oKey = `${o.symbol}_${normalizeStrategy(o.strategy)}`;
                if (oKey === aggregationKey) {
                    actualQty += Math.abs(parseFloat(o.qty || 0));
                }
            });

            const sideStr = String(order.side || "").toLowerCase();
            const isSell = sideStr === "sell" || sideStr === "2" || sideStr === "-1";

            rows.push({
                symbol: order.symbol,
                side: isSell ? "SELL" : "BUY",
                strategy: normalizeStrategy(order.strategy),
                index: getAlpacaUnderlying(order.symbol),
                baseQty,
                multiplier,
                orderQty: Math.abs(parseFloat(order.qty || 0)),
                expectedQty,
                actualQty,
                stop_price: order.stop_price || order.stopPrice || "-",
                limit_price: order.limit_price || order.limitPrice || "-",
                order_id: order.order_id || order.orderId || "N/A",
                orderdatetime: order.orderdatetime || order.orderDateTime || "N/A",
                match: actualQty === expectedQty,
                strike: extractStrike(order.symbol),
                status: order.status || "",
                originalOrder: order
            });
        });

        return rows;
    }, [orders, clientData, symbolStrategyBaseQty, users]);

    // Filter by Asset
    const [selectedAsset, setSelectedAsset] = useState("ALL");

    const uniqueAssets = useMemo(() => {
        const assets = new Set(ordersBySymbolStrategy.map(r => r.index).filter(Boolean));
        return ["ALL", ...Array.from(assets).sort()];
    }, [ordersBySymbolStrategy]);

    const filteredRows = useMemo(() => {
        if (selectedAsset === "ALL") return ordersBySymbolStrategy;
        return ordersBySymbolStrategy.filter(r => r.index === selectedAsset);
    }, [ordersBySymbolStrategy, selectedAsset]);

    const toggleOrderSelection = (orderId) => {
        setSelectedOrderIds(prev => {
            const next = new Set(prev);
            if (next.has(orderId)) next.delete(orderId);
            else next.add(orderId);
            return next;
        });
    };

    const handleCancelSelected = () => {
        const toCancel = filteredRows
            .filter(r => selectedOrderIds.has(r.order_id))
            .map(r => r.originalOrder);

        if (toCancel.length === 0) {
            toast.error("Please select orders to cancel.");
            return;
        }

        setOrdersToCancel(toCancel);
        setSuccessModalActionType("Cancel");
        setConfirmModalOpen(true);
    };

    const handleModifySelected = () => {
        const toModify = filteredRows
            .filter(r => selectedOrderIds.has(r.order_id))
            .map(r => r.originalOrder);

        if (toModify.length === 0) {
            toast.error("Please select orders to modify.");
            return;
        }

        setOrdersToModify(toModify);
        setModifyModalOpen(true);
    };

    const handleCancelSingle = (order) => {
        setOrdersToCancel([order]);
        setSuccessModalActionType("Cancel");
        setConfirmModalOpen(true);
    };

    const handleConfirmCancel = async () => {
        if (!ordersToCancel.length) return;
        setIsCancelling(true);
        try {
            const payloads = ordersToCancel.map(o => ({
                orderid: String(o.orderId || o.order_id),
                clientid: clientData.clientId,
                broker: "alpaca"
            }));

            const response = await apiClient.post(`${server.godseye.baseUrl}manual_cancel_orders`, payloads);
            if (response.status === 200) {
                const rawOrders = response.data?.data || response.data?.orders || payloads;
                const responseOrders = rawOrders.map(o => {
                    const match = ordersToCancel.find(c => String(c.orderId || c.order_id) === String(o.orderid || o.order_id || o.orderId));
                    const isSuccess = o.status === "success" || o.status === "SUCCESS";
                    const isFail = o.status === "failed" || o.status === "FAILED" || o.status === "rejected";
                    return {
                        ...o,
                        orderId: o.orderid || o.order_id || o.orderId,
                        clientId: o.clientid || o.client_id || clientData?.clientId,
                        status: isFail ? "Rejected" : (isSuccess ? "CANCELED" : (o.status || "CANCELED")),
                        broker: o.broker || "alpaca",
                        symbol: match?.symbol || ordersToCancel[0]?.symbol || "N/A",
                        rejectReason: o.message || o.response?.message || (isFail ? "Cancellation failed" : "")
                    };
                });
                setSuccessModalActionType("Cancel");
                setSubmittedOrders(responseOrders);
                toast.success("Alpaca order cancellation processed!");
                setConfirmModalOpen(false);
                setSuccessModalOpen(true);
            } else {
                toast.error(response.data?.message || "Failed to cancel orders");
            }
        } catch (error) {
            console.error("Error cancelling Alpaca orders:", error);
            toast.error("Error cancelling orders");
        } finally {
            setIsCancelling(false);
        }
    };

    const handleRemoveFromBatch = (orderId) => {
        setOrdersToCancel(prev => {
            const next = prev.filter(o => String(o.order_id || o.orderId) !== String(orderId));
            if (next.length === 0) setConfirmModalOpen(false);
            return next;
        });
    };

    const formatOrderTime = (dtStr) => {
        if (!dtStr || dtStr === "N/A") return "-";
        try {
            const date = new Date(dtStr);
            return date.toLocaleString("en-IN", {
                timeZone: "Asia/Kolkata",
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
            });
        } catch {
            return dtStr;
        }
    };

    const formatPrice = (num) => {
        if (num === undefined || num === null || num === 0 || num === "-") return "-";
        return Number(num).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const handleSingleModify = (row) => {
        setOrdersToModify([row.originalOrder]);
        setModifyModalOpen(true);
    };

    if (!isOpen || !clientData) return null;

    return (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-[60] backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-[#1a1d29] rounded-xl border border-gray-800 w-full max-w-7xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-700/50 flex justify-between items-center bg-[#14161f]">
                    <div>
                        <h2 className="text-white text-lg font-bold flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-[#FCD72B]"></span>
                            Client details: {clientData.clientName} ({clientData.clientId})
                        </h2>
                        <p className="text-gray-400 text-xs mt-1">Review and compare working orders with target consensus</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-full text-gray-400 transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-4 sleek-scrollbar">
                    {/* Bulk Action Panel */}
                    <div className="flex justify-between items-center bg-[#13151f] p-3 rounded-lg border border-gray-800">
                        <div className="text-xs text-gray-400 font-bold uppercase tracking-wider">
                            Selected: <span className="text-blue-400 font-extrabold">{selectedOrderIds.size}</span> orders
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={handleCancelSelected}
                                className="px-3 py-1.5 bg-red-900/20 hover:bg-red-800/40 text-red-400 text-[10px] font-bold rounded border border-red-800/30 transition-all uppercase tracking-wide"
                            >
                                Cancel Selected
                            </button>
                            <button
                                onClick={handleModifySelected}
                                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-[10px] font-bold rounded border border-indigo-400/30 transition-all uppercase tracking-wide flex items-center gap-1.5"
                            >
                                <Pencil className="w-3 h-3" /> Modify Selected
                            </button>
                        </div>
                    </div>

                    <div className="bg-[#0f1116] border border-gray-800 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs border-collapse">
                                <thead className="bg-[#14161f] text-gray-400 font-bold uppercase sticky top-0">
                                    <tr className="border-b border-gray-800">
                                        <th className="w-8 px-2 text-center">
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 rounded border-gray-700 bg-gray-900 text-blue-600 focus:ring-blue-500"
                                                checked={filteredRows.length > 0 && filteredRows.every(r => selectedOrderIds.has(r.order_id))}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedOrderIds(new Set(filteredRows.map(r => r.order_id)));
                                                    } else {
                                                        setSelectedOrderIds(new Set());
                                                    }
                                                }}
                                            />
                                        </th>
                                        <th className="py-2.5 px-3">Order ID</th>
                                        <th className="py-2.5 px-3">Time</th>
                                        <th className="py-2.5 px-3">
                                            <div className="flex items-center gap-2">
                                                <span>Asset</span>
                                                <select
                                                    value={selectedAsset}
                                                    onChange={(e) => setSelectedAsset(e.target.value)}
                                                    className="bg-[#1a1d29] border border-gray-700 text-gray-300 text-[10px] rounded px-1.5 py-0.5 focus:outline-none"
                                                >
                                                    {uniqueAssets.map(a => <option key={a} value={a}>{a}</option>)}
                                                </select>
                                            </div>
                                        </th>
                                        <th className="py-2.5 px-3">Side</th>
                                        <th className="py-2.5 px-3">Symbol</th>
                                        <th className="py-2.5 px-3">Strategy</th>
                                        <th className="py-2.5 px-3 text-center">Strike</th>
                                        <th className="py-2.5 px-3 text-right">Stop Price</th>
                                        <th className="py-2.5 px-3 text-right">Limit Price</th>
                                        <th className="py-2.5 px-3 text-center">Base Qty (1x)</th>
                                        <th className="py-2.5 px-3 text-center">Multiplier</th>
                                        <th className="py-2.5 px-3 text-center">Order Qty</th>
                                        <th className="py-2.5 px-3 text-center">Expected</th>
                                        <th className="py-2.5 px-3 text-center">Actual</th>
                                        <th className="py-2.5 px-3 text-center">Match</th>
                                        <th className="py-2.5 px-3 text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-800/45 text-gray-300">
                                    {filteredRows.length === 0 ? (
                                        <tr>
                                            <td colSpan="16" className="py-8 text-center text-gray-500 font-medium">
                                                No matching active orders found
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredRows.map((row, idx) => {
                                            const qtyColor = row.match ? "text-green-400" : "text-red-400";
                                            return (
                                                <tr key={`${row.order_id}_${idx}`} className="hover:bg-white/[0.01]">
                                                    <td className="px-2 text-center">
                                                        <input
                                                            type="checkbox"
                                                            className="w-3.5 h-3.5 rounded border-gray-850 bg-gray-950 text-blue-600 focus:ring-blue-500"
                                                            checked={selectedOrderIds.has(row.order_id)}
                                                            onChange={() => toggleOrderSelection(row.order_id)}
                                                        />
                                                    </td>
                                                    <td className="py-2 px-3 font-mono text-gray-400 text-[10px] truncate max-w-[100px]" title={row.order_id}>{row.order_id}</td>
                                                    <td className="py-2 px-3 font-mono text-gray-400">{formatOrderTime(row.orderdatetime)}</td>
                                                    <td className="py-2 px-3 text-blue-400 font-bold">{row.index}</td>
                                                    <td className="py-2 px-3 font-bold text-[10px]">
                                                        <span className={`px-1.5 py-0.5 rounded font-bold ${row.side === "SELL" ? "bg-red-500/10 text-red-400 border border-red-500/20" : "bg-green-500/10 text-green-400 border border-green-500/20"}`}>{row.side}</span>
                                                    </td>
                                                    <td className="py-2 px-3 font-semibold text-white">{row.symbol}</td>
                                                    <td className="py-2 px-3 text-gray-400 font-semibold">{row.strategy}</td>
                                                    <td className="py-2 px-3 text-center font-mono">{row.strike}</td>
                                                    <td className="py-2 px-3 text-right font-mono">${formatPrice(row.stop_price)}</td>
                                                    <td className="py-2 px-3 text-right font-mono">${formatPrice(row.limit_price)}</td>
                                                    <td className="py-2 px-3 text-center font-mono">{row.baseQty}</td>
                                                    <td className="py-2 px-3 text-center font-mono">{row.multiplier}</td>
                                                    <td className="py-2 px-3 text-center font-mono font-bold text-white">{row.orderQty}</td>
                                                    <td className="py-2 px-3 text-center font-mono font-semibold text-gray-400">{row.expectedQty}</td>
                                                    <td className={`py-2 px-3 text-center font-mono font-bold ${qtyColor}`}>{row.actualQty}</td>
                                                    <td className="py-2 px-3 text-center font-bold">
                                                        {row.match ? (
                                                            <span className="text-green-400">✔</span>
                                                        ) : (
                                                            <span className="text-red-400">✖</span>
                                                        )}
                                                    </td>
                                                    <td className="py-2 px-3 text-center">
                                                        <div className="flex items-center justify-center gap-1">
                                                            <button
                                                                onClick={() => handleSingleModify(row)}
                                                                className="p-1 text-blue-400 hover:text-white hover:bg-blue-600/20 border border-blue-800/50 rounded transition-all"
                                                                title="Modify Order"
                                                            >
                                                                <Pencil className="w-3.5 h-3.5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleCancelSingle(row.originalOrder)}
                                                                className="px-2 py-0.5 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 text-[10px] font-medium transition"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Confirm Cancel Modal */}
                {confirmModalOpen && (
                    <CancelConfirmationModal
                        isOpen={confirmModalOpen}
                        onClose={() => setConfirmModalOpen(false)}
                        onConfirm={handleConfirmCancel}
                        orders={ordersToCancel}
                        onDelete={handleRemoveFromBatch}
                    />
                )}

                {/* Exit Success Modal */}
                {successModalOpen && (
                    <AlpacaExitSuccessModal
                        isOpen={successModalOpen}
                        onClose={() => {
                            setSuccessModalOpen(false);
                            onClose();
                        }}
                        orders={submittedOrders}
                        actionType={successModalActionType}
                    />
                )}

                {/* Bulk Modify Modal */}
                {modifyModalOpen && (
                    <AlpacaBulkModifyModal
                        isOpen={modifyModalOpen}
                        onClose={() => setModifyModalOpen(false)}
                        orders={ordersToModify}
                        onSuccess={(orders) => {
                            setModifyModalOpen(false);
                            setSuccessModalActionType("Modify");
                            setSubmittedOrders(orders);
                            setSuccessModalOpen(true);
                        }}
                    />
                )}
            </div>
        </div>
    );
}
